import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeliveryDriver } from '../DeliveryDriversLayout';
import { User, Calendar, Building2 } from 'lucide-react';
import { DeliveryMap, type DriverMarkerData } from '@/components/DeliveryMap';
import { useRealtimeStore } from '@/stores';

export default function DeliveryDriverOverview() {
  const { t } = useTranslation(['admin', 'common']);
  const { driver } = useDeliveryDriver();
  const realtimeStore = useRealtimeStore();
  const [driverMarker, setDriverMarker] = useState<DriverMarkerData | null>(null);
  const [, setTick] = useState(0);

  // Update timestamps every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Get driver location from realtime store
  useEffect(() => {
    if (!driver) return;

    const updateDriverLocation = () => {
      const driverLocation = realtimeStore.getDriverLocation(driver.id);
      if (driverLocation) {
        setDriverMarker({
          id: driverLocation.driver_id,
          name: driverLocation.driver_name || driver.display_name || driver.full_name,
          latitude: driverLocation.latitude,
          longitude: driverLocation.longitude,
          isOnDelivery: !!driverLocation.current_order_id,
          lastUpdated: driverLocation.timestamp,
        });
      } else {
        setDriverMarker(null);
      }
    };

    // Initial load
    updateDriverLocation();

    // Subscribe to updates
    const unsubscribe = realtimeStore.onDriverLocation((location) => {
      if (location.driver_id === driver.id) {
        setDriverMarker({
          id: location.driver_id,
          name: location.driver_name || driver.display_name || driver.full_name,
          latitude: location.latitude,
          longitude: location.longitude,
          isOnDelivery: !!location.current_order_id,
          lastUpdated: location.timestamp,
        });
      }
    });

    return () => unsubscribe();
  }, [driver, realtimeStore]);

  if (!driver) {
    return null;
  }

  const formatGender = (gender: string | null | undefined) => {
    if (!gender) return t('deliveryDrivers.notSpecified');
    const genderKey = gender.toLowerCase();
    if (genderKey === 'male') return t('deliveryDrivers.male');
    if (genderKey === 'female') return t('deliveryDrivers.female');
    if (genderKey === 'other') return t('deliveryDrivers.other');
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  return (
    <div className="flex flex-col xl:flex-row gap-6">
      {/* Left side - Driver details */}
      <div className="space-y-6 xl:flex-1">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <User className="w-4 h-4" />
              <span>{t('deliveryDrivers.gender')}</span>
            </div>
            <p className="text-lg font-medium">{formatGender(driver.gender)}</p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Calendar className="w-4 h-4" />
              <span>{t('deliveryDrivers.birthdate')}</span>
            </div>
            <p className="text-lg font-medium">
              {driver.birthdate || t('deliveryDrivers.notSpecified')}
            </p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Building2 className="w-4 h-4" />
              <span>{t('deliveryDrivers.assignedStations')}</span>
            </div>
            <p className="text-lg font-medium">{driver.gas_station_ids.length}</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">{t('deliveryDrivers.driverInformation')}</h3>
          <dl className="space-y-3">
            <div className="flex flex-col sm:flex-row border-b pb-2 gap-1 sm:gap-0">
              <dt className="sm:w-40 text-muted-foreground text-sm sm:text-base">{t('deliveryDrivers.displayName')}</dt>
              <dd className="flex-1 font-medium">
                {driver.display_name || driver.full_name}
                {driver.display_name && (
                  <span className="ml-2 text-xs text-muted-foreground">({t('deliveryDrivers.custom')})</span>
                )}
              </dd>
            </div>
            <div className="flex flex-col sm:flex-row border-b pb-2 gap-1 sm:gap-0">
              <dt className="sm:w-40 text-muted-foreground text-sm sm:text-base">{t('deliveryDrivers.actualName')}</dt>
              <dd className="flex-1">{driver.full_name}</dd>
            </div>
            <div className="flex flex-col sm:flex-row border-b pb-2 gap-1 sm:gap-0">
              <dt className="sm:w-40 text-muted-foreground text-sm sm:text-base">ID</dt>
              <dd className="flex-1 font-mono text-sm break-all">{driver.id}</dd>
            </div>
            <div className="flex flex-col sm:flex-row border-b pb-2 gap-1 sm:gap-0">
              <dt className="sm:w-40 text-muted-foreground text-sm sm:text-base">{t('deliveryDrivers.gender')}</dt>
              <dd className="flex-1">{formatGender(driver.gender)}</dd>
            </div>
            <div className="flex flex-col sm:flex-row border-b pb-2 gap-1 sm:gap-0">
              <dt className="sm:w-40 text-muted-foreground text-sm sm:text-base">{t('deliveryDrivers.birthdate')}</dt>
              <dd className="flex-1">{driver.birthdate || t('deliveryDrivers.notSpecified')}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Right side - Driver Location Map */}
      <div className="xl:w-[28rem] xl:shrink-0">
        <div className="xl:sticky xl:top-4">
          <h3 className="text-lg font-semibold mb-4">{t('deliveryDrivers.currentLocation')}</h3>
          {driverMarker ? (
            <DeliveryMap
              drivers={[driverMarker]}
              className="h-64 xl:h-80 rounded-lg overflow-hidden border"
            />
          ) : (
            <div className="h-64 xl:h-80 rounded-lg border bg-muted flex items-center justify-center">
              <p className="text-muted-foreground">{t('deliveryDrivers.noLocationData')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
